import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-hot-toast";
import apiService from "../services/api";
import { formatCurrency } from "../utils/currencyUtils";
import { triggerBlobDownload } from "../utils/downloadUtils";
import {
  PayrollTable,
  MonthHeader,
  LoadingState,
  ErrorState,
  PendingAdjustmentsPanel,
  LockedPayrollAdjustmentsPanel,
} from "../components/month-detail";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatMonth = (yearMonth) => {
  if (!yearMonth) return "";
  const [year, month] = yearMonth.split("-");
  return new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString(
    "en-US",
    { year: "numeric", month: "long" },
  );
};

function Spinner({ size = 16, color = "white" }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: `2px solid rgba(${color === "white" ? "255,255,255" : "0,18,114"},0.3)`,
        borderTop: `2px solid ${color === "white" ? "white" : "#001272"}`,
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        flexShrink: 0,
      }}
    />
  );
}

const LINE_TYPE_LABEL = {
  late_report_penalty: "Late Report Penalty",
  regular_cancel_penalty: "Regular Cancel Penalty",
  regular_noshow_penalty: "Regular No-Show Penalty",
  severe_cancel_penalty: "Severe Cancel Penalty",
  severe_noshow_penalty: "Severe No-Show Penalty",
};

// ─── ValidationResult ─────────────────────────────────────────────────────────

function ValidationResult({ result, isLoading, yearMonth }) {
  const [expandedSection, setExpandedSection] = useState(null);
  const queryClient = useQueryClient();
  const { data: exemptedTcIds = [] } = useQuery({
    queryKey: ["lateReportExemptions", yearMonth],
    queryFn: () => apiService.getLateReportExemptions(yearMonth),
    enabled: !!yearMonth,
  });
  const exemptedSet = new Set(exemptedTcIds);

  const handleToggleWaive = async (p) => {
    try {
      if (exemptedSet.has(p.appointmentId)) {
        await apiService.removeLateReportExemption(p.appointmentId, p.branchId);
      } else {
        await apiService.addLateReportExemption(p.appointmentId, p.branchId);
      }
      queryClient.invalidateQueries({ queryKey: ["lateReportExemptions", yearMonth] });
    } catch (e) {
      toast.error("Failed to update exemption: " + e.message);
    }
  };

  if (isLoading)
    return (
      <div
        style={{
          fontSize: "0.8rem",
          color: "#94a3b8",
          fontStyle: "italic",
          marginTop: "0.75rem",
        }}
      >
        Loading validation result...
      </div>
    );
  if (!result || !result.yearMonth) return null;

  const blockingErrors = result.blockingErrors || [];
  const missingReports = result.missingReports || [];
  const tutors = result.tutors || [];
  const penalties = result.penalties || [];
  const demoConversions = result.demoConversions || [];
  const missingMentors = result.missingMentors || [];
  const missingCounselors = result.missingCounselors || [];
  const monthlyHourBonuses = result.monthlyHourBonuses || [];
  const hasErrors = blockingErrors.length > 0 || missingReports.length > 0;

  const lateReportPenalties = penalties.filter(
    (p) => p.penaltyType === "late_report_penalty",
  );
  const regularCancelPenalties = penalties.filter(
    (p) =>
      p.penaltyType === "regular_cancel_penalty" ||
      p.penaltyType === "regular_noshow_penalty",
  );
  const severeCancelPenalties = penalties.filter(
    (p) =>
      p.penaltyType === "severe_cancel_penalty" ||
      p.penaltyType === "severe_noshow_penalty",
  );
  const toggle = (s) => setExpandedSection(expandedSection === s ? null : s);

  const badge = (color, text) => (
    <span
      style={{
        display: "inline-block",
        fontSize: "0.7rem",
        fontWeight: "600",
        padding: "0.1rem 0.45rem",
        borderRadius: "9999px",
        background:
          color === "red"
            ? "#fee2e2"
            : color === "amber"
              ? "#fef3c7"
              : color === "green"
                ? "#dcfce7"
                : color === "purple"
                  ? "#f3e8ff"
                  : "#dbeafe",
        color:
          color === "red"
            ? "#991b1b"
            : color === "amber"
              ? "#854d0e"
              : color === "green"
                ? "#166534"
                : color === "purple"
                  ? "#6b21a8"
                  : "#1e40af",
      }}
    >
      {text}
    </span>
  );

  // variant: undefined = blue/gray, "error" = red, "warn" = amber
  const section = (id, label, count, rows, variant) => {
    const border =
      variant === "error"
        ? "#fca5a5"
        : variant === "warn"
          ? "#fcd34d"
          : "#e2e8f0";
    const bgActive =
      variant === "error"
        ? "#fef2f2"
        : variant === "warn"
          ? "#fffbeb"
          : "#f0f9ff";
    const bgIdle =
      variant === "error"
        ? "#fff5f5"
        : variant === "warn"
          ? "#fefce8"
          : "#f8fafc";
    const color =
      variant === "error"
        ? "#991b1b"
        : variant === "warn"
          ? "#854d0e"
          : "#001272";
    return (
      <div
        style={{
          border: `1px solid ${border}`,
          borderRadius: "0.375rem",
          marginBottom: "0.4rem",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0.45rem 0.75rem",
            cursor: "pointer",
            background: expandedSection === id ? bgActive : bgIdle,
            fontSize: "0.8rem",
            fontWeight: "600",
            color,
            userSelect: "none",
          }}
          onClick={() => toggle(id)}
        >
          <span>
            {label} ({count})
          </span>
          <span style={{ fontSize: "0.7rem" }}>
            {expandedSection === id ? "▲" : "▼"}
          </span>
        </div>
        {expandedSection === id && (
          <div style={{ padding: "0.4rem 0.5rem" }}>{rows}</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: "0.75rem" }}>
      {/* Summary badges */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.35rem",
          marginBottom: "0.65rem",
        }}
      >
        {blockingErrors.length > 0 &&
          badge(
            "red",
            `${blockingErrors.length} blocking error${blockingErrors.length !== 1 ? "s" : ""}`,
          )}
        {missingReports.length > 0 &&
          badge(
            "red",
            `${missingReports.length} missing report${missingReports.length !== 1 ? "s" : ""}`,
          )}
        {badge("green", `${result.totalCompletedRegular} completed regular`)}
        {badge("red", `${result.totalCancelled} cancelled`)}
        {badge("amber", `${result.totalCancelledChargeable} cancelled chargeable`)}
        {badge("purple", `${result.totalDemoLessons} demos`)}
      </div>

      {/* Blocking errors — always expanded */}
      {hasErrors && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: "0.375rem",
            padding: "0.75rem",
            marginBottom: "0.5rem",
          }}
        >
          <div
            style={{
              fontSize: "0.8rem",
              fontWeight: "700",
              color: "#991b1b",
              marginBottom: "0.4rem",
            }}
          >
            Blocking Errors — must be resolved before generating payroll
          </div>
          {blockingErrors.map((e, i) => (
            <div
              key={i}
              style={{
                fontSize: "0.75rem",
                color: "#7f1d1d",
                borderTop: i > 0 ? "1px solid #fca5a5" : "none",
                paddingTop: i > 0 ? "0.3rem" : 0,
                marginTop: i > 0 ? "0.3rem" : 0,
              }}
            >
              <strong>{e.type}</strong> — {e.description}
              {e.studentName && (
                <span style={{ color: "#b91c1c" }}> ({e.studentName})</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Missing Reports — blocking, always expanded */}
      {missingReports.length > 0 && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: "0.375rem",
            padding: "0.75rem",
            marginBottom: "0.5rem",
          }}
        >
          <div
            style={{
              fontSize: "0.8rem",
              fontWeight: "700",
              color: "#991b1b",
              marginBottom: "0.4rem",
            }}
          >
            Missing Reports — must be resolved before generating payroll
          </div>
          {missingReports.map((r, i) => (
            <div
              key={i}
              style={{
                fontSize: "0.75rem",
                color: "#7f1d1d",
                borderTop: i > 0 ? "1px solid #fca5a5" : "none",
                paddingTop: i > 0 ? "0.3rem" : 0,
                marginTop: i > 0 ? "0.3rem" : 0,
              }}
            >
              <strong>{r.tutorName}</strong>
              {r.topic && <span> — {r.topic}</span>}
              <span style={{ color: "#b91c1c" }}>
                {r.appointmentDate &&
                  ` · ${new Date(r.appointmentDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                {r.branchName && ` · ${r.branchName}`}
                {r.appointmentId && ` · TC #${r.appointmentId}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Missing counselors — collapsible, amber */}
      {missingCounselors.length > 0 &&
        section(
          "counselors",
          "Missing Counselors",
          missingCounselors.length,
          <div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#854d0e",
                marginBottom: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Below students had lesson(s) in {result.yearMonth} but no
              counselors assigned
            </div>
            {missingCounselors.map((s, i) => (
              <div
                key={i}
                style={{
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.35rem",
                  borderBottom:
                    i < missingCounselors.length - 1
                      ? "1px solid #fde68a"
                      : "none",
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontWeight: "500", color: "#78350f" }}>
                  {s.studentName}
                </span>
                {s.studentId != null && (
                  <span style={{ color: "#94a3b8", fontSize: "0.7rem" }}>
                    TC #{s.studentId}
                  </span>
                )}
              </div>
            ))}
          </div>,
          "warn",
        )}

      {missingMentors.length > 0 &&
        section(
          "mentors",
          "Missing Mentors",
          missingMentors.length,
          <div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#854d0e",
                marginBottom: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Below tutors had lesson(s) in {result.yearMonth} but no mentor
              assigned — mentor earnings will not be generated
            </div>
            {missingMentors.map((m, i) => (
              <div
                key={i}
                style={{
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.35rem",
                  borderBottom:
                    i < missingMentors.length - 1
                      ? "1px solid #fde68a"
                      : "none",
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontWeight: "500", color: "#78350f" }}>
                  {m.tutorName}
                </span>
                <span style={{ color: "#94a3b8", fontSize: "0.7rem" }}>
                  {m.lessonCount} lesson{m.lessonCount !== 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>,
          "warn",
        )}

      {tutors.length > 0 &&
        section(
          "tutors",
          "Tutors with lessons",
          tutors.length,
          tutors.map((t, i) => (
            <div
              key={i}
              style={{
                fontSize: "0.75rem",
                padding: "0.3rem 0.35rem",
                borderBottom:
                  i < tutors.length - 1 ? "1px solid #f1f5f9" : "none",
                display: "flex",
                justifyContent: "space-between",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontWeight: "500",
                  color: t.isDefaultRate ? "#64748b" : "#001272",
                }}
              >
                {t.tutorName}
              </span>
              <span style={{ color: "#64748b" }}>
                {"rate: "}
                <span
                  style={{
                    color: t.isDefaultRate ? "#94a3b8" : "#001272",
                    fontStyle: t.isDefaultRate ? "italic" : "normal",
                  }}
                >
                  {t.hourlyRate}
                </span>
                {" · "}
                {t.regularLessons} reg · {t.demoLessons} demo
                {t.cancelNoshowPenalties > 0 && (
                  <span style={{ color: "#dc2626" }}>
                    {" "}
                    · {t.cancelNoshowPenalties}×penalty
                  </span>
                )}
                {t.demoConversionBonuses > 0 && (
                  <span style={{ color: "#059669" }}>
                    {" "}
                    · {t.demoConversionBonuses}×conv
                  </span>
                )}
              </span>
            </div>
          )),
        )}

      {(() => {
        const groupByTutor = (list) => {
          const map = new Map();
          list.forEach((p) => {
            const key = p.canonicalTutorId || p.tutorName || "Unknown";
            if (!map.has(key))
              map.set(key, { tutorName: p.tutorName, items: [] });
            map.get(key).items.push(p);
          });
          return Array.from(map.values());
        };

        const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
        const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : null;
        const sortByDate = (items) => [...items].sort((a, b) => {
          if (!a.appointmentDate && !b.appointmentDate) return 0;
          if (!a.appointmentDate) return 1;
          if (!b.appointmentDate) return -1;
          return new Date(a.appointmentDate) - new Date(b.appointmentDate);
        });

        const penaltyRows = (list) => {
          const groups = groupByTutor(list);
          return groups.map((g, gi) => (
            <div key={gi} style={{ marginBottom: gi < groups.length - 1 ? "0.5rem" : 0 }}>
              <div style={{ fontSize: "0.72rem", fontWeight: "700", color: "#dc2626", padding: "0.2rem 0.35rem", background: "#fff1f2", borderRadius: "0.25rem", marginBottom: "0.25rem" }}>
                {g.tutorName || "Unknown Tutor"} ({g.items.length})
              </div>
              {sortByDate(g.items).map((p, i) => (
                <div key={i} style={{ fontSize: "0.75rem", padding: "0.25rem 0.35rem 0.25rem 0.75rem", borderBottom: i < g.items.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "500", color: "#b91c1c" }}>{LINE_TYPE_LABEL[p.penaltyType] || p.penaltyType}</span>
                    {p.estimatedAmount != null && <span style={{ color: "#dc2626", fontWeight: "600" }}>{formatCurrency(p.estimatedAmount)}</span>}
                  </div>
                  <div style={{ color: "#64748b" }}>
                    {fmtDateTime(p.appointmentDate)}
                    {p.topic && <span> · {p.topic}</span>}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: "0.7rem" }}>
                    {p.branchName && <span>{p.branchName} · </span>}
                    {p.appointmentId && <span>TC #{p.appointmentId}</span>}
                  </div>
                  {p.penaltyType === "late_report_penalty" && (
                    <div style={{ fontSize: "0.7rem", marginTop: "0.15rem" }}>
                      {p.appointmentFinishTime && (
                        <div style={{ color: "#94a3b8" }}>Lesson ended: <strong>{fmtDateTime(p.appointmentFinishTime)}</strong></div>
                      )}
                      {p.reportSubmittedAt
                        ? (() => {
                            const lateMs = new Date(p.reportSubmittedAt) - new Date(p.appointmentFinishTime);
                            const totalMin = Math.floor(lateMs / 60000);
                            const d = Math.floor(totalMin / 1440);
                            const h = Math.floor((totalMin % 1440) / 60);
                            const m = totalMin % 60;
                            const parts = [];
                            if (d > 0) parts.push(`${d}d`);
                            if (h > 0) parts.push(`${h}h`);
                            if (m > 0 || parts.length === 0) parts.push(`${m}m`);
                            return <div style={{ color: "#b45309" }}>Report submitted: <strong>{fmtDateTime(p.reportSubmittedAt)}</strong> (after {parts.join(" ")})</div>;
                          })()
                        : <div style={{ color: "#b45309" }}>Submitted late</div>}
                      {(() => {
                        const isWaived = exemptedSet.has(p.appointmentId);
                        return (
                          <button
                            onClick={() => handleToggleWaive(p)}
                            style={{
                              marginTop: "0.25rem",
                              fontSize: "0.68rem",
                              fontWeight: "600",
                              padding: "0.1rem 0.5rem",
                              borderRadius: "9999px",
                              border: "1px solid",
                              cursor: "pointer",
                              background: isWaived ? "#fef3c7" : "#f0fdf4",
                              borderColor: isWaived ? "#f59e0b" : "#86efac",
                              color: isWaived ? "#92400e" : "#166534",
                            }}
                          >
                            {isWaived ? "Waived — click to unwaive" : "Waive penalty"}
                          </button>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ));
        };

        return (
          <>
            {lateReportPenalties.length > 0 &&
              section(
                "penalties-late",
                "Late Reports",
                lateReportPenalties.length,
                penaltyRows(lateReportPenalties),
                "error",
              )}
            {regularCancelPenalties.length > 0 &&
              section(
                "penalties-cancel",
                "No-shows & Cancellations",
                regularCancelPenalties.length,
                penaltyRows(regularCancelPenalties),
                "error",
              )}
            {severeCancelPenalties.length > 0 &&
              section(
                "penalties-severe",
                "Severe No-shows & Cancellations",
                severeCancelPenalties.length,
                penaltyRows(severeCancelPenalties),
                "error",
              )}
          </>
        );
      })()}

      {monthlyHourBonuses.length > 0 && section("hour-bonuses", "Monthly Hours Bonuses", monthlyHourBonuses.length,
        monthlyHourBonuses.map((b, i) => (
          <div key={i} style={{ fontSize: "0.75rem", padding: "0.3rem 0.35rem", borderBottom: i < monthlyHourBonuses.length - 1 ? "1px solid #f1f5f9" : "none", display: "flex", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
            <span style={{ fontWeight: "500", color: "#001272" }}>{b.tutorName}</span>
            <span style={{ color: "#64748b" }}>
              {b.totalHours.toFixed(2)}h · <span style={{ color: "#059669", fontWeight: "600" }}>{b.tier} → {formatCurrency(b.bonusAmount)}</span>
            </span>
          </div>
        ))
      )}

      {demoConversions.length > 0 &&
        section(
          "conversions",
          "Demo Conversions",
          demoConversions.length,
          demoConversions.map((c, i) => (
            <div
              key={i}
              style={{
                fontSize: "0.75rem",
                padding: "0.3rem 0.35rem",
                borderBottom:
                  i < demoConversions.length - 1 ? "1px solid #f1f5f9" : "none",
              }}
            >
              <div>
                <span style={{ fontWeight: "500", color: "#059669" }}>
                  {c.tutorName}
                </span>
              </div>
              <div style={{ color: "#64748b" }}>
                {c.appointmentDate &&
                  new Date(c.appointmentDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                {c.topic && ` · ${c.topic}`}
                {c.convertedYearMonth && ` → converted ${c.convertedYearMonth}`}
              </div>
            </div>
          )),
        )}
    </div>
  );
}

// ─── StepBar ──────────────────────────────────────────────────────────────────

function StepBar({ steps }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        marginBottom: "1.5rem",
      }}
    >
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "0.3rem",
              flex: i < steps.length - 1 ? "none" : 1,
              minWidth: 70,
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "700",
                fontSize: "0.85rem",
                backgroundColor: s.done
                  ? "#16a34a"
                  : s.active
                    ? "#001272"
                    : "#e2e8f0",
                color: s.done || s.active ? "white" : "#94a3b8",
              }}
            >
              {s.done ? "✓" : s.n}
            </div>
            <span
              style={{
                fontSize: "0.68rem",
                fontWeight: "600",
                textAlign: "center",
                color: s.done ? "#16a34a" : s.active ? "#001272" : "#94a3b8",
                lineHeight: 1.2,
              }}
            >
              {s.title}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              style={{
                flex: 1,
                height: 2,
                backgroundColor: s.done ? "#16a34a" : "#e2e8f0",
                marginTop: 16,
                alignSelf: "flex-start",
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Email Dispatch Panel ─────────────────────────────────────────────────────

const EmailDispatchPanel = ({ yearMonth }) => {
  const { data: emailStatuses = [] } = useQuery({
    queryKey: ["emailStatus", yearMonth],
    queryFn: () => apiService.getEmailStatus(yearMonth),
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const sentCount = emailStatuses.filter((e) => e.status === "SENT").length;
  const pendingCount = emailStatuses.filter((e) => e.status === "PENDING" || e.status === "PROCESSING").length;
  const failedCount = emailStatuses.filter((e) => e.status === "FAILED").length;
  const totalQueued = emailStatuses.length;

  return (
    <div>
      <div
        style={{
          background: "#f0fdf4",
          border: "1px solid #86efac",
          borderRadius: "0.375rem",
          padding: "0.75rem",
          fontSize: "0.85rem",
          color: "#166534",
          marginBottom: "0.65rem",
        }}
      >
        🔒 Payroll is locked. No further changes allowed.
      </div>
      <div
        style={{
          background: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: "0.375rem",
          padding: "0.75rem",
          fontSize: "0.85rem",
          color: "#0369a1",
        }}
      >
        <strong>Pay Statements</strong>
        {totalQueued > 0 ? (
          <span style={{ marginLeft: "0.5rem", fontSize: "0.78rem", color: "#64748b" }}>
            {sentCount} sent
            {pendingCount > 0 && ` · ${pendingCount} queued`}
            {failedCount > 0 && ` · ${failedCount} failed`}
          </span>
        ) : (
          <span style={{ marginLeft: "0.5rem", fontSize: "0.78rem", color: "#64748b" }}>
            Not yet sent
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const MonthDetailPage = () => {
  const { yearMonth } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  // Job polling IDs
  const [syncJobId, setSyncJobId] = useState(null);
  const [valJobId, setValJobId] = useState(null);
  const [genJobId, setGenJobId] = useState(null);

  // Which completed steps are expanded by the user
  const [expandedDoneSteps, setExpandedDoneSteps] = useState(new Set());
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [generateModal, setGenerateModal] = useState(null); // null | { force: bool }
  const toggleDoneStep = (n) =>
    setExpandedDoneSteps((prev) => {
      const next = new Set(prev);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });

  // ── Queries ──

  const {
    data: syncStatus,
    isLoading: isLoadingSyncStatus,
    error: syncStatusError,
  } = useQuery({
    queryKey: ["syncStatus", yearMonth],
    queryFn: async () => {
      try {
        return await apiService.getSyncStatus(yearMonth);
      } catch {
        return {
          yearMonth,
          tutorSync: null,
          studentSync: null,
          appointmentSync: null,
          noteSync: null,
          reportSync: null,
          overallStatus: "UNKNOWN",
        };
      }
    },
    select: (data) => {
      const ok = (s) => s?.lastSync && s?.successful && s?.status === "SUCCESS";
      return {
        tutors: ok(data.tutorSync)
          ? "SYNCED"
          : data.tutorSync?.lastSync
            ? "FAILED"
            : "NOT_SYNCED",
        tutorDetails: data.tutorSync
          ? { ...data.tutorSync, tutorCount: data.tutorSync.entityCount || 0 }
          : null,
        students: ok(data.studentSync)
          ? "SYNCED"
          : data.studentSync?.lastSync
            ? "FAILED"
            : "NOT_SYNCED",
        studentDetails: data.studentSync
          ? {
              ...data.studentSync,
              studentCount: data.studentSync.entityCount || 0,
            }
          : null,
        appointments: ok(data.appointmentSync)
          ? "SYNCED"
          : data.appointmentSync?.lastSync
            ? "FAILED"
            : "NOT_SYNCED",
        appointmentDetails: data.appointmentSync
          ? {
              ...data.appointmentSync,
              appointmentCount: data.appointmentSync.entityCount || 0,
            }
          : null,
        notes: ok(data.noteSync)
          ? "SYNCED"
          : data.noteSync?.lastSync
            ? "FAILED"
            : "NOT_SYNCED",
        noteDetails: data.noteSync
          ? { ...data.noteSync, noteCount: data.noteSync.entityCount || 0 }
          : null,
        reports: ok(data.reportSync)
          ? "SYNCED"
          : data.reportSync?.lastSync
            ? "FAILED"
            : "NOT_SYNCED",
        reportDetails: data.reportSync
          ? {
              ...data.reportSync,
              reportCount: data.reportSync.entityCount || 0,
            }
          : null,
        overallStatus: data.overallStatus,
      };
    },
    retry: 1,
    retryDelay: 1000,
  });

  const {
    data: payrollRun,
    isLoading: isLoadingPayrollRun,
    error: payrollRunError,
    refetch: refetchPayrollRun,
  } = useQuery({
    queryKey: ["payrollRun", yearMonth],
    queryFn: () => apiService.getPayrollRun(yearMonth),
    staleTime: 30 * 1000,
  });

  const { data: eligibility } = useQuery({
    queryKey: ["payrollEligibility", yearMonth],
    queryFn: () => apiService.getPayrollEligibility(yearMonth),
    staleTime: 60 * 1000,
  });

  const {
    data: validationResult,
    isLoading: isLoadingValidation,
    refetch: refetchValidation,
  } = useQuery({
    queryKey: ["payrollValidation", yearMonth],
    queryFn: () => apiService.getPayrollValidation(yearMonth),
    staleTime: 60 * 1000,
  });

  // Job pollers
  const makeJobQuery = (jobId) => ({
    queryKey: ["jobStatus", jobId],
    queryFn: () => apiService.getJobStatus(jobId),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "COMPLETED" || s === "FAILED" ? false : 2000;
    },
  });

  const { data: syncJobStatus } = useQuery(makeJobQuery(syncJobId));
  const { data: valJobStatus } = useQuery(makeJobQuery(valJobId));
  const { data: genJobStatus } = useQuery(makeJobQuery(genJobId));

  // Job completion effects
  useEffect(() => {
    if (syncJobStatus?.status === "COMPLETED") {
      queryClient.invalidateQueries({ queryKey: ["syncStatus", yearMonth] });
      setSyncJobId(null);
      toast.success("Sync completed");
    } else if (syncJobStatus?.status === "FAILED") {
      setSyncJobId(null);
      toast.error(
        `Sync failed: ${syncJobStatus.errorMessage || "Unknown error"}`,
      );
    }
  }, [syncJobStatus?.status]); // eslint-disable-line

  useEffect(() => {
    if (valJobStatus?.status === "COMPLETED") {
      refetchValidation();
      setValJobId(null);
      toast.success("Validation complete");
    } else if (valJobStatus?.status === "FAILED") {
      setValJobId(null);
      toast.error(
        `Validation failed: ${valJobStatus.errorMessage || "Unknown error"}`,
      );
    }
  }, [valJobStatus?.status]); // eslint-disable-line

  useEffect(() => {
    if (genJobStatus?.status === "COMPLETED") {
      refetchPayrollRun();
      queryClient.invalidateQueries({ queryKey: ["payrollTutors", yearMonth] });
      queryClient.invalidateQueries({ queryKey: ["payrollLineItems", yearMonth] });
      setGenJobId(null);
      toast.success("Payroll generated!");
    } else if (genJobStatus?.status === "FAILED") {
      setGenJobId(null);
      toast.error(
        `Generation failed: ${genJobStatus.errorMessage || "Unknown error"}`,
      );
    }
  }, [genJobStatus?.status]); // eslint-disable-line

  // ── Handlers ──

  const handleSync = async (force = false) => {
    try {
      const job = await apiService.startFullSync(
        yearMonth,
        force ? { force: true } : {},
      );
      setSyncJobId(job.jobId);
    } catch (err) {
      toast.error(`Failed to start sync: ${err.message}`);
    }
  };

  const handleValidate = async () => {
    try {
      const job = await apiService.validatePayroll(yearMonth);
      setValJobId(job.jobId);
    } catch (err) {
      toast.error(`Failed to start validation: ${err.message}`);
    }
  };

  const handleGenerate = async (force = false) => {
    setGenerateModal({ force });
  };

  const confirmGenerate = async () => {
    const force = generateModal.force;
    setGenerateModal(null);
    try {
      const job = await apiService.generatePayroll(yearMonth, force);
      setGenJobId(job.jobId);
    } catch (err) {
      toast.error(`Failed to start generation: ${err.message}`);
    }
  };

  const handleLock = async () => {
    try {
      await apiService.lockPayroll(yearMonth);
      setLockModalOpen(false);
      refetchPayrollRun();
      queryClient.invalidateQueries({ queryKey: ["payrollRun", yearMonth] });
      toast.success("Payroll locked.");
    } catch (err) {
      setLockModalOpen(false);
      toast.error(`Failed to lock: ${err.message}`);
    }
  };

  // ── Derived state ──

  const getMonthDataStatus = () => {
    if (syncStatusError || !syncStatus) return "UNKNOWN";
    if (syncStatus.overallStatus === "MISSING_TUTORS") return "MISSING_TUTORS";
    const ok = (d) => d?.lastSync && d?.successful && d?.status === "SUCCESS";
    if (
      ok(syncStatus.tutorDetails) &&
      ok(syncStatus.studentDetails) &&
      ok(syncStatus.appointmentDetails) &&
      ok(syncStatus.noteDetails) &&
      ok(syncStatus.reportDetails)
    )
      return "COMPLETE";
    if (
      ok(syncStatus.tutorDetails) ||
      ok(syncStatus.studentDetails) ||
      ok(syncStatus.appointmentDetails) ||
      ok(syncStatus.noteDetails) ||
      ok(syncStatus.reportDetails)
    )
      return "PARTIAL";
    return "NOT_SYNCED";
  };

  const monthDataStatus = getMonthDataStatus();
  const isSyncing = !!syncJobId;
  const isSynced =
    monthDataStatus === "COMPLETE" || monthDataStatus === "HISTORICAL";
  const isValidating = !!valJobId;
  const isValidated = !!(
    validationResult?.yearMonth &&
    (!validationResult.blockingErrors ||
      validationResult.blockingErrors.length === 0) &&
    (!validationResult.missingReports ||
      validationResult.missingReports.length === 0)
  );
  const isGenerating = !!genJobId;
  const isGenerated = payrollRun != null;
  const isDraft = payrollRun?.status === "DRAFT";
  const isLocked = payrollRun?.status === "LOCKED";
  const isMonthInProgress = yearMonth === new Date().toISOString().slice(0, 7);
  const hasBlockingErrors =
    (validationResult?.blockingErrors?.length || 0) > 0 ||
    (validationResult?.missingReports?.length || 0) > 0;

  // Step 4 is the target once generated (lock it); otherwise step is first incomplete
  const activeStep = isLocked
    ? 4
    : isGenerated
      ? 4
      : isValidated
        ? 3
        : isSynced
          ? 2
          : 1;

  // ── Style helpers ──

  const btn = (bg, disabled) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.4rem",
    border: "none",
    borderRadius: "0.375rem",
    padding: "0.7rem 1.25rem",
    fontWeight: "600",
    fontSize: "0.875rem",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    backgroundColor: bg,
    color: "white",
    transition: "background-color 0.15s",
  });

  const cardBorder = (active, done) =>
    active
      ? "2px solid #001272"
      : done
        ? "1px solid #bbf7d0"
        : "1px solid #e2e8f0";

  const cardBg = (active, done) => (done && !active ? "#f0fdf4" : "white");

  const stepCircle = (n, active, done, locked) => (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "700",
        fontSize: "0.8rem",
        backgroundColor: locked
          ? "#e2e8f0"
          : done
            ? "#16a34a"
            : active
              ? "#001272"
              : "#e2e8f0",
        color: locked ? "#94a3b8" : "white",
      }}
    >
      {done ? "✓" : n}
    </div>
  );

  const currentBadge = (
    <span
      style={{
        fontSize: "0.7rem",
        backgroundColor: "#dbeafe",
        color: "#1e40af",
        padding: "0.15rem 0.5rem",
        borderRadius: "9999px",
        fontWeight: "600",
      }}
    >
      Current
    </span>
  );

  const [zipDownloading, setZipDownloading] = useState(false);

  const handleDownloadZip = async () => {
    setZipDownloading(true);
    try {
      const blob = await apiService.downloadPayrollZip(yearMonth);
      triggerBlobDownload(blob, `payroll_${yearMonth}.zip`);
    } catch (err) {
      toast.error("Download failed: " + err.message);
    } finally {
      setZipDownloading(false);
    }
  };

  const payrollRunStats = payrollRun ? (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "0.375rem",
        padding: "0.75rem",
        marginBottom: "0.75rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0.4rem",
        }}
      >
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#64748b",
          }}
        >
          Payroll Run
        </span>
        <span
          style={{
            fontSize: "0.68rem",
            fontWeight: "600",
            padding: "0.12rem 0.45rem",
            borderRadius: "9999px",
            backgroundColor: isLocked ? "#dcfce7" : "#fef9c3",
            color: isLocked ? "#166534" : "#854d0e",
          }}
        >
          {payrollRun.status}
        </span>
      </div>
      {[
        ["Gross", payrollRun.totalGross, "#001272"],
        ["Deductions", payrollRun.totalDeductions, "#dc2626"],
        ...(payrollRun.totalAdjustments && parseFloat(payrollRun.totalAdjustments) !== 0
          ? [["Adjustments", payrollRun.totalAdjustments, parseFloat(payrollRun.totalAdjustments) >= 0 ? "#7e22ce" : "#dc2626"]]
          : []),
        ["Net", payrollRun.totalNet, "#0369a1"],
      ].map(([label, val, color]) => (
        <div
          key={label}
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.82rem",
            padding: "0.12rem 0",
            borderBottom: label !== "Net" ? "1px solid #f1f5f9" : "none",
          }}
        >
          <span style={{ color: "#64748b" }}>{label}</span>
          <span
            style={{
              fontWeight: label === "Net" ? "700" : "600",
              color,
              fontSize: label === "Net" ? "0.88rem" : "0.82rem",
            }}
          >
            {formatCurrency(val)}
          </span>
        </div>
      ))}
      {payrollRun.generatedAt && (
        <div
          style={{
            fontSize: "0.68rem",
            color: "#94a3b8",
            marginTop: "0.35rem",
          }}
        >
          Generated{" "}
          {new Date(payrollRun.generatedAt).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
          {payrollRun.generatedBy && ` by ${payrollRun.generatedBy}`}
        </div>
      )}
      {isLocked && (
        <button
          onClick={handleDownloadZip}
          disabled={zipDownloading}
          style={{
            marginTop: "0.65rem",
            display: "flex",
            alignItems: "center",
            gap: "0.35rem",
            background: zipDownloading ? "#e2e8f0" : "#001272",
            color: zipDownloading ? "#64748b" : "white",
            border: "none",
            borderRadius: "0.375rem",
            padding: "0.45rem 0.9rem",
            fontSize: "0.78rem",
            fontWeight: "600",
            cursor: zipDownloading ? "not-allowed" : "pointer",
          }}
        >
          {zipDownloading ? (
            <>
              <Spinner size={13} />
              Preparing ZIP…
            </>
          ) : (
            "↓ Download All PDFs (ZIP)"
          )}
        </button>
      )}
    </div>
  ) : null;

  // ── Loading / Error ──

  if (isLoadingSyncStatus || isLoadingPayrollRun)
    return (
      <div style={{ padding: "0.75rem", margin: "0 auto" }}>
        <MonthHeader
          yearMonth={yearMonth}
          formatMonth={formatMonth}
          navigate={navigate}
        />
        <LoadingState />
      </div>
    );

  if (payrollRunError)
    return (
      <div style={{ padding: "0.75rem", margin: "0 auto" }}>
        <MonthHeader
          yearMonth={yearMonth}
          formatMonth={formatMonth}
          navigate={navigate}
        />
        <ErrorState
          error={payrollRunError}
          onRetry={() =>
            queryClient.invalidateQueries({
              queryKey: ["payrollRun", yearMonth],
            })
          }
        />
      </div>
    );

  // ── Render ──

  return (
    <div style={{ padding: "0.75rem", margin: "0 auto" }}>
      <MonthHeader
        yearMonth={yearMonth}
        formatMonth={formatMonth}
        navigate={navigate}
      />

      {/* ── Step Bar ── */}
      <StepBar
        steps={[
          { n: 1, title: "Sync", done: isSynced, active: activeStep === 1 },
          {
            n: 2,
            title: "Validate",
            done: isValidated,
            active: activeStep === 2,
          },
          {
            n: 3,
            title: "Generate",
            done: isGenerated,
            active: activeStep === 3,
          },
          { n: 4, title: "Lock", done: isLocked, active: activeStep === 4 },
        ]}
      />

      {/* ════════════════════════════════════════════
          Step 1 — Sync Data
          ════════════════════════════════════════════ */}
      {(() => {
        const active = activeStep === 1;
        const expanded = active || ((isDraft || isSynced || isLocked) && !expandedDoneSteps.has(1));
        return (
          <div
            style={{
              border: cardBorder(active, isSynced),
              borderRadius: "0.5rem",
              marginBottom: "0.75rem",
              background: cardBg(active, isSynced),
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.9rem 1.25rem",
                cursor: (isSynced || isDraft || isLocked) ? "pointer" : "default",
                background: active ? "#f0f4ff" : "transparent",
              }}
              onClick={(isSynced || isDraft || isLocked) ? () => toggleDoneStep(1) : undefined}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.65rem",
                }}
              >
                {stepCircle(1, active, isSynced, false)}
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "0.92rem",
                      color: isSynced ? "#16a34a" : "#001272",
                    }}
                  >
                    Sync Data
                  </div>
                  {isSynced && !expandedDoneSteps.has(1) && (
                    <div style={{ fontSize: "0.72rem", color: "#4ade80" }}>
                      {syncStatus?.tutorDetails?.tutorCount || 0} tutors ·{" "}
                      {syncStatus?.studentDetails?.studentCount || 0} students ·{" "}
                      {syncStatus?.appointmentDetails?.appointmentCount || 0}{" "}
                      appts · {syncStatus?.noteDetails?.noteCount || 0} notes ·{" "}
                      {syncStatus?.reportDetails?.reportCount || 0} reports
                    </div>
                  )}
                </div>
              </div>
              {active && currentBadge}
              {(isSynced || isDraft || isLocked) && (
                <span style={{ fontSize: "0.7rem", color: "#64748b" }}>
                  {expandedDoneSteps.has(1) ? "▼" : "▲"}
                </span>
              )}
            </div>

            {expanded && (
              <div style={{ padding: "0 1.25rem 1.25rem" }}>
                {/* Status grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.6rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  {[
                    [
                      "Tutors",
                      syncStatus?.tutors === "SYNCED",
                      syncStatus?.tutorDetails?.tutorCount,
                      syncStatus?.tutorDetails?.lastSync,
                    ],
                    [
                      "Students",
                      syncStatus?.students === "SYNCED",
                      syncStatus?.studentDetails?.studentCount,
                      syncStatus?.studentDetails?.lastSync,
                    ],
                    [
                      "Appointments",
                      syncStatus?.appointments === "SYNCED",
                      syncStatus?.appointmentDetails?.appointmentCount,
                      syncStatus?.appointmentDetails?.lastSync,
                    ],
                    [
                      "Notes",
                      syncStatus?.notes === "SYNCED",
                      syncStatus?.noteDetails?.noteCount,
                      syncStatus?.noteDetails?.lastSync,
                    ],
                    [
                      "Reports",
                      syncStatus?.reports === "SYNCED",
                      syncStatus?.reportDetails?.reportCount,
                      syncStatus?.reportDetails?.lastSync,
                    ],
                  ].map(([label, synced, count, lastSync]) => (
                    <div
                      key={label}
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "0.375rem",
                        padding: "0.65rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.72rem",
                          color: "#64748b",
                          marginBottom: "0.2rem",
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "0.82rem",
                          color: synced ? "#16a34a" : "#dc2626",
                        }}
                      >
                        {synced ? "✓ Synced" : "✗ Not synced"}
                      </div>
                      {lastSync && (
                        <div
                          style={{
                            fontSize: "0.68rem",
                            color: "#94a3b8",
                            marginTop: "0.15rem",
                          }}
                        >
                          {count != null && `${count} · `}
                          {new Date(lastSync).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Sync job progress */}
                {isSyncing && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      background: "#dbeafe",
                      border: "1px solid #93c5fd",
                      borderRadius: "0.375rem",
                      padding: "0.5rem 0.75rem",
                      marginBottom: "0.6rem",
                      fontSize: "0.8rem",
                      color: "#1e40af",
                    }}
                  >
                    <Spinner size={13} color="blue" />
                    <span>
                      {syncJobStatus?.progressMessage || "Syncing..."}
                    </span>
                    {syncJobStatus?.progressPercentage > 0 && (
                      <span style={{ marginLeft: "auto", fontWeight: "600" }}>
                        {syncJobStatus.progressPercentage}%
                      </span>
                    )}
                  </div>
                )}

                {!isLocked && (!isSynced ? (
                  <>
                    <button
                      style={btn("#001272", isSyncing || isMonthInProgress)}
                      disabled={isSyncing || isMonthInProgress}
                      onClick={() => handleSync(false)}
                      onMouseEnter={(e) => {
                        if (!isSyncing && !isMonthInProgress)
                          e.currentTarget.style.backgroundColor = "#000d5c";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSyncing && !isMonthInProgress)
                          e.currentTarget.style.backgroundColor = "#001272";
                      }}
                      title={isMonthInProgress ? "Month is still in progress" : ""}
                    >
                      {isSyncing ? (
                        <>
                          <Spinner />
                          Syncing...
                        </>
                      ) : (
                        "Sync Data"
                      )}
                    </button>
                    {isMonthInProgress && (
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#6b7280" }}>
                        Month is still in progress.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      style={{
                        ...btn("#dc2626", isSyncing || isMonthInProgress),
                        fontSize: "0.82rem",
                        padding: "0.5rem 1rem",
                      }}
                      disabled={isSyncing || isMonthInProgress}
                      onClick={() => handleSync(true)}
                      onMouseEnter={(e) => {
                        if (!isSyncing && !isMonthInProgress)
                          e.currentTarget.style.backgroundColor = "#b91c1c";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSyncing && !isMonthInProgress)
                          e.currentTarget.style.backgroundColor = "#dc2626";
                      }}
                      title={isMonthInProgress ? "Month is still in progress" : ""}
                    >
                      {isSyncing ? (
                        <>
                          <Spinner />
                          Resyncing...
                        </>
                      ) : (
                        "Force Resync"
                      )}
                    </button>
                    {isMonthInProgress && (
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#6b7280" }}>
                        Month is still in progress.
                      </p>
                    )}
                  </>
                ) : null)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════════════
          Step 2 — Validate
          ════════════════════════════════════════════ */}
      {(() => {
        const locked = !isSynced && !isDraft && !isLocked;
        const active = activeStep === 2;
        // Step 2 stays expanded whenever synced, in draft, or locked; user can collapse via header click
        const expanded = active || ((isDraft || isSynced || isLocked) && !expandedDoneSteps.has(2));
        return (
          <div
            style={{
              border: cardBorder(active, isValidated),
              borderRadius: "0.5rem",
              marginBottom: "0.75rem",
              background: locked ? "#fafafa" : cardBg(active, isValidated),
              overflow: "hidden",
              opacity: locked ? 0.55 : 1,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.9rem 1.25rem",
                cursor: !locked && !active ? "pointer" : "default",
                background: active ? "#f0f4ff" : "transparent",
              }}
              onClick={
                !locked && !active ? () => toggleDoneStep(2) : undefined
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.65rem",
                }}
              >
                {stepCircle(2, active, isValidated, locked)}
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "0.92rem",
                      color: locked
                        ? "#94a3b8"
                        : isValidated
                          ? "#16a34a"
                          : "#001272",
                    }}
                  >
                    Validate
                  </div>
                  {isValidated && !expandedDoneSteps.has(2) && (
                    <div style={{ fontSize: "0.72rem", color: "#4ade80" }}>
                      {validationResult?.tutors?.length || 0} tutors ·{" "}
                      {validationResult?.penalties?.length || 0} penalties · no
                      blocking errors
                    </div>
                  )}
                  {locked && (
                    <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                      Sync data first
                    </div>
                  )}
                </div>
              </div>
              {active && currentBadge}
              {!locked && !active && (
                <span style={{ fontSize: "0.7rem", color: "#64748b" }}>
                  {expandedDoneSteps.has(2) ? "▼" : "▲"}
                </span>
              )}
            </div>

            {!locked && expanded && (
              <div style={{ padding: "0 1.25rem 1.25rem" }}>
                {!isLocked && <button
                  style={btn("#0369a1", isValidating || isGenerating)}
                  disabled={isValidating || isGenerating}
                  onClick={handleValidate}
                  onMouseEnter={(e) => {
                    if (!isValidating && !isGenerating)
                      e.currentTarget.style.backgroundColor = "#075985";
                  }}
                  onMouseLeave={(e) => {
                    if (!isValidating && !isGenerating)
                      e.currentTarget.style.backgroundColor = "#0369a1";
                  }}
                >
                  {isValidating ? (
                    <>
                      <Spinner />
                      {valJobStatus?.progressMessage || "Validating..."}
                    </>
                  ) : isValidated ? (
                    "Re-run Validation"
                  ) : (
                    "Run Validation"
                  )}
                </button>}

                {!isValidating && (
                  <ValidationResult
                    result={validationResult}
                    isLoading={isLoadingValidation}
                    yearMonth={yearMonth}
                  />
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════════════
          Step 3 — Generate Payroll
          ════════════════════════════════════════════ */}
      {(() => {
        const locked = !isValidated;
        const active = activeStep === 3;
        const expanded = active || expandedDoneSteps.has(3);
        return (
          <div
            style={{
              border: cardBorder(active, isGenerated),
              borderRadius: "0.5rem",
              marginBottom: "0.75rem",
              background: locked ? "#fafafa" : cardBg(active, isGenerated),
              overflow: "hidden",
              opacity: locked ? 0.55 : 1,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.9rem 1.25rem",
                cursor: isGenerated && !active ? "pointer" : "default",
                background: active ? "#f0f4ff" : "transparent",
              }}
              onClick={
                isGenerated && !active ? () => toggleDoneStep(3) : undefined
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.65rem",
                }}
              >
                {stepCircle(3, active, isGenerated, locked)}
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "0.92rem",
                      color: locked
                        ? "#94a3b8"
                        : isGenerated
                          ? "#16a34a"
                          : "#001272",
                    }}
                  >
                    Generate Payroll
                  </div>
                  {isGenerated && !expandedDoneSteps.has(3) && (
                    <div style={{ fontSize: "0.72rem", color: "#4ade80" }}>
                      {payrollRun?.status} · Net{" "}
                      {formatCurrency(payrollRun?.totalNet)}
                    </div>
                  )}
                  {locked && (
                    <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                      Validate first
                    </div>
                  )}
                </div>
              </div>
              {active && currentBadge}
              {isGenerated && !active && (
                <span style={{ fontSize: "0.7rem", color: "#64748b" }}>
                  {expandedDoneSteps.has(3) ? "▲" : "▼"}
                </span>
              )}
            </div>

            {!locked && expanded && (
              <div style={{ padding: "0 1.25rem 1.25rem" }}>
                {payrollRunStats}

                {!isLocked && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    {!isGenerated && (
                      <>
                        <button
                          style={btn(
                            "#001272",
                            isGenerating || hasBlockingErrors || eligibility?.eligible === false,
                          )}
                          disabled={isGenerating || hasBlockingErrors || eligibility?.eligible === false}
                          onClick={() => handleGenerate(false)}
                          onMouseEnter={(e) => {
                            if (!isGenerating && !hasBlockingErrors && eligibility?.eligible !== false)
                              e.currentTarget.style.backgroundColor = "#000d5c";
                          }}
                          onMouseLeave={(e) => {
                            if (!isGenerating && !hasBlockingErrors && eligibility?.eligible !== false)
                              e.currentTarget.style.backgroundColor = "#001272";
                          }}
                          title={
                            hasBlockingErrors
                              ? "Resolve blocking errors in the Validate step first"
                              : eligibility?.reason || ""
                          }
                        >
                          {isGenerating ? (
                            <>
                              <Spinner />
                              {genJobStatus?.progressMessage || "Generating..."}
                            </>
                          ) : (
                            "Generate Payroll"
                          )}
                        </button>
                        {eligibility?.reason && (
                          <p style={{ margin: 0, fontSize: "0.8rem", color: "#6b7280" }}>
                            {eligibility.reason}
                          </p>
                        )}
                      </>
                    )}

                    {isDraft && (
                      <button
                        style={{
                          ...btn("#dc2626", isGenerating),
                          fontSize: "0.82rem",
                          padding: "0.5rem 1rem",
                        }}
                        disabled={isGenerating}
                        onClick={() => handleGenerate(true)}
                        onMouseEnter={(e) => {
                          if (!isGenerating)
                            e.currentTarget.style.backgroundColor = "#b91c1c";
                        }}
                        onMouseLeave={(e) => {
                          if (!isGenerating)
                            e.currentTarget.style.backgroundColor = "#dc2626";
                        }}
                      >
                        {isGenerating ? (
                          <>
                            <Spinner />
                            Regenerating...
                          </>
                        ) : (
                          "Force Regenerate"
                        )}
                      </button>
                    )}

                    {isGenerating && genJobStatus && (
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#64748b",
                          textAlign: "center",
                        }}
                      >
                        {genJobStatus.progressMessage || "Processing..."}
                        {genJobStatus.progressPercentage > 0 &&
                          ` (${genJobStatus.progressPercentage}%)`}
                      </div>
                    )}

                    {hasBlockingErrors && !isGenerating && (
                      <div
                        style={{
                          background: "#fef2f2",
                          border: "1px solid #fca5a5",
                          borderRadius: "0.375rem",
                          padding: "0.5rem 0.75rem",
                          fontSize: "0.78rem",
                          color: "#991b1b",
                        }}
                      >
                        Resolve blocking errors in the Validate step before
                        generating payroll.
                      </div>
                    )}
                  </div>
                )}

                {isLocked && (
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#64748b",
                      fontStyle: "italic",
                    }}
                  >
                    Payroll is locked — regeneration not allowed.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Pre-Generation Adjustments ── */}
      <PendingAdjustmentsPanel yearMonth={yearMonth} isLocked={isLocked} />

      {/* ── Payroll Table ── */}
      {isGenerated && (
        <div style={{ marginTop: "1rem", marginBottom: "0.75rem" }}>
          <PayrollTable yearMonth={yearMonth} payrollRun={payrollRun} isSuperAdmin={isSuperAdmin} />
        </div>
      )}

      {/* ── Locked Payroll Adjustments & Waivers ── */}
      {isLocked && <LockedPayrollAdjustmentsPanel yearMonth={yearMonth} />}

      {/* ════════════════════════════════════════════
          Step 4 — Lock Payroll
          ════════════════════════════════════════════ */}
      {(() => {
        const locked = !isGenerated;
        const active = activeStep === 4;
        return (
          <div
            style={{
              border: cardBorder(active, isLocked),
              borderRadius: "0.5rem",
              marginBottom: "0.75rem",
              background: locked ? "#fafafa" : cardBg(active, isLocked),
              overflow: "hidden",
              opacity: locked ? 0.55 : 1,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.9rem 1.25rem",
                background: active ? "#f0f4ff" : "transparent",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.65rem",
                }}
              >
                {stepCircle(4, active, isLocked, locked)}
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "0.92rem",
                      color: locked
                        ? "#94a3b8"
                        : isLocked
                          ? "#16a34a"
                          : "#001272",
                    }}
                  >
                    Lock Payroll
                  </div>
                  {isLocked && (
                    <div style={{ fontSize: "0.72rem", color: "#4ade80" }}>
                      Locked · Net {formatCurrency(payrollRun?.totalNet)}
                    </div>
                  )}
                  {locked && (
                    <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                      Generate payroll first
                    </div>
                  )}
                  {!locked && !isLocked && !active && (
                    <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                      Ready to lock
                    </div>
                  )}
                </div>
              </div>
              {active && !isLocked && currentBadge}
            </div>

            {!locked && active && (
              <div style={{ padding: "0 1.25rem 1.25rem" }}>
                {payrollRunStats}

                {isLocked ? (
                  <EmailDispatchPanel yearMonth={yearMonth} />
                ) : (
                  <>
                    <div
                      style={{
                        background: "#fef9c3",
                        border: "1px solid #fde68a",
                        borderRadius: "0.375rem",
                        padding: "0.5rem 0.75rem",
                        fontSize: "0.78rem",
                        color: "#854d0e",
                        marginBottom: "0.65rem",
                      }}
                    >
                      ⚠️ Locking is permanent — payroll cannot be regenerated
                      after locking. Recommended only after the payroll has been
                      processed.
                    </div>
                    <button
                      style={btn("#374151", false)}
                      onClick={() => setLockModalOpen(true)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#1f2937";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#374151";
                      }}
                    >
                      🔒 Lock Payroll
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Generate Confirmation Modal ── */}
      {generateModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => setGenerateModal(null)}
        >
          <div style={{ background: "white", borderRadius: "0.5rem", padding: "1.75rem", maxWidth: 420, width: "calc(100% - 2rem)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: "1.1rem", fontWeight: "700", color: "#001272", marginBottom: "0.5rem" }}>
              {generateModal.force ? "Force Regenerate Payroll" : "Generate Payroll"}
            </div>
            <p style={{ fontSize: "0.875rem", color: "#4b5563", margin: "0 0 0.75rem" }}>
              {generateModal.force
                ? <>You are about to <strong>delete and regenerate</strong> payroll for <strong>{formatMonth(yearMonth)}</strong>. The existing draft will be discarded.</>
                : <>Generate payroll for <strong>{formatMonth(yearMonth)}</strong>?</>}
            </p>
            {generateModal.force && (
              <p style={{ fontSize: "0.875rem", color: "#92400e", margin: "0 0 1.25rem", background: "#fef9c3", border: "1px solid #fde68a", borderRadius: "0.375rem", padding: "0.5rem 0.75rem" }}>
                ⚠️ The existing draft payroll will be permanently deleted before regenerating.
              </p>
            )}
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: generateModal.force ? 0 : "1.25rem" }}>
              <button
                style={{ padding: "0.6rem 1.25rem", border: "1px solid #e2e8f0", borderRadius: "0.375rem", background: "white", color: "#374151", fontWeight: "600", fontSize: "0.875rem", cursor: "pointer" }}
                onClick={() => setGenerateModal(null)}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
              >
                Cancel
              </button>
              <button
                style={{ padding: "0.6rem 1.25rem", border: "none", borderRadius: "0.375rem", background: generateModal.force ? "#dc2626" : "#001272", color: "white", fontWeight: "600", fontSize: "0.875rem", cursor: "pointer" }}
                onClick={confirmGenerate}
                onMouseEnter={(e) => { e.currentTarget.style.background = generateModal.force ? "#b91c1c" : "#000d5c"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = generateModal.force ? "#dc2626" : "#001272"; }}
              >
                {generateModal.force ? "Yes, Regenerate" : "Yes, Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lock Confirmation Modal ── */}
      {lockModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setLockModalOpen(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "0.5rem",
              padding: "1.75rem",
              maxWidth: 420,
              width: "calc(100% - 2rem)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                color: "#001272",
                marginBottom: "0.5rem",
              }}
            >
              🔒 Lock Payroll
            </div>
            <p
              style={{
                fontSize: "0.875rem",
                color: "#4b5563",
                margin: "0 0 0.5rem",
              }}
            >
              You are about to lock payroll for{" "}
              <strong>{formatMonth(yearMonth)}</strong>.
            </p>
            <p
              style={{
                fontSize: "0.875rem",
                color: "#991b1b",
                margin: "0 0 1.25rem",
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: "0.375rem",
                padding: "0.5rem 0.75rem",
              }}
            >
              ⚠️ This action is <strong>permanent and irreversible</strong>.
              Payroll cannot be regenerated after locking. Recommended only
              after the payroll has been processed.
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                style={{
                  padding: "0.6rem 1.25rem",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                  background: "white",
                  color: "#374151",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
                onClick={() => setLockModalOpen(false)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f8fafc";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: "0.6rem 1.25rem",
                  border: "none",
                  borderRadius: "0.375rem",
                  background: "#374151",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
                onClick={handleLock}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#1f2937";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#374151";
                }}
              >
                Yes, Lock Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @media (max-width: 768px) { [data-responsive-title-row] { flex-direction: column !important; gap: 1rem !important; } [data-responsive-back-button] { align-self: flex-start !important; } }
      `}</style>
    </div>
  );
};

export default MonthDetailPage;
